import React, { ChangeEvent, FC, useState } from 'react'
import { CardElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexRow } from '@/atoms/FlexContainers'
import { ArrowLeftIcon } from '@/atoms/Icons/ArrowLeftIcon'
import { Input } from '@/atoms/Input'
import { TitleSM } from '@/atoms/Text'
import { useThemeContext } from '@/contexts/ThemeContext'
import { PayPalPayment } from '@/molecules/PayPalPayment'
import { PaymentInfo } from '@/services/PifCheckoutService'
import { isSupportedPayPalCurrency, isSupportedStripeCurrency, Offer } from '@/services/PifService/PifService'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { getDecimalsForCurrency } from '@/services/PriceFormatter/PriceFormatter'
import { isValidEmail, isValidName } from '@/utils/StripeFormValidator'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import ExpressProcessingOverlay from '@/views/ExpressCheckoutView/components/ExpressProcessingOverlay'
import { PrefilledCreditCardInput } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/PrefilledCreditCardInput'
import { useAppleGooglePay } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useAppleGooglePay'
import { usePayItForwardForm } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm'
import { hasGuildUpsell } from '../../utils'

interface Props {
  offer: Offer
  clientSecret: string
  isGuest: boolean
  projectSlug: string
  guildMembershipIncluded?: boolean
}

export const ExpressStripeForm: FC<Props> = ({
  offer,
  isGuest,
  clientSecret,
  projectSlug,
  guildMembershipIncluded = false,
}) => {
  const { t } = useTranslate('pif')
  const { isDarkMode } = useThemeContext()
  const [shouldOnlyShowCreditCardForm, setShouldOnlyShowCreditCardForm] = useState(false)
  const price = formatPrice(offer.price, { includeDecimals: false, currency: offer.currency })
  const router = useSafeRouter()

  const {
    name,
    setName,
    email,
    setEmail,
    errorMessage,
    isSubmitting,
    savedPaymentInfo,
    setSavedPaymentInfo,
    checkout,
    onPayItForwardComplete,
    bimClient,
  } = usePayItForwardForm()

  const {
    isSubmittingWithWallet,
    errorMessageWithWallet,
    paymentRequest: walletPaymentRequest,
    isFetchingUserDetails,
  } = useAppleGooglePay({
    onComplete: (arg) => {
      onPayItForwardComplete(arg)
    },
    offer,
    isGuest,
    clientSecret,
    fetchUserDetails: hasGuildUpsell(typeof router.query.projectSlug === 'string' ? router.query.projectSlug : ''),
  })

  return (
    <>
      {(isSubmitting || isFetchingUserDetails) && <ExpressProcessingOverlay />}
      <form className="mx-auto w-full" onSubmit={checkout}>
        {!shouldOnlyShowCreditCardForm && (
          <>
            {!guildMembershipIncluded && isSupportedPayPalCurrency(offer.currency) && (
              <PayPalPayment
                name={projectSlug}
                onComplete={onPayItForwardComplete}
                description={`Angel Studios: ${projectSlug}:pif`}
                softDescriptor={`${projectSlug || 'Pay it Forward'}`}
                sku={offer?.slug || name}
                currency={offer.currency.toUpperCase()}
                amount={getPriceForCurrency(offer.price, offer.currency).toFixed(
                  getDecimalsForCurrency(offer.currency),
                )}
                buttonHeight={50}
                isGuest={isGuest}
                bimClient={bimClient}
              />
            )}
            {walletPaymentRequest && isSupportedStripeCurrency(offer.currency) && (
              <div className="h-50 mb-[7px] mt-1 rounded">
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest: walletPaymentRequest,
                    style: { paymentRequestButton: { height: '50px' } },
                  }}
                />
              </div>
            )}
            {isSupportedStripeCurrency(offer.currency) && !shouldOnlyShowCreditCardForm && (
              <Button
                onClick={() => setShouldOnlyShowCreditCardForm((state) => !state)}
                className="h-[50px] w-full rounded"
                variant={isDarkMode ? 'white' : 'black'}
              >
                {t('creditOrDebitCard', 'Credit or Debit Card')}
              </Button>
            )}
          </>
        )}

        {shouldOnlyShowCreditCardForm && (
          <div>
            <FlexRow className="relative my-4 gap-2">
              <div
                onClick={() => {
                  setShouldOnlyShowCreditCardForm(false)
                }}
                className="absolute left-0 top-0 flex w-fit cursor-pointer flex-row justify-end rounded-full bg-white p-1.5"
              >
                <ArrowLeftIcon color="core-gray-700" size={18} />
              </div>
              <TitleSM className="mx-auto" weight="bold" color={isDarkMode ? 'white' : 'core-gray-950'}>
                {t('creditOrDebitCard', 'Credit or Debit Card')}
              </TitleSM>
            </FlexRow>
            <Input
              required
              usePlaceholderAsLabel
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              label={t('nameOnCard', 'Name on card')}
              isValid={isValidName(name)}
              inputClasses="!border-core-gray-200 border w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent focus-visible:border-oxide focus-visible:border-b-2"
              className="photon-paragraph-md mt-1 rounded bg-white px-2 py-2.5 text-core-gray-950"
              labelClassName={classNames('photon-caption-sm', isDarkMode ? 'text-white' : 'text-core-gray-950')}
            />
            <Input
              required
              usePlaceholderAsLabel
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              label={t('emailAddress', 'Email')}
              isValid={isValidEmail(email)}
              inputClasses="!border-core-gray-200 border w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent focus-visible:border-oxide focus-visible:border-b-2"
              className="photon-paragraph-md mt-1 rounded bg-white px-2 py-2.5 text-core-gray-950"
              labelClassName={classNames('photon-caption-sm', isDarkMode ? 'text-white' : 'text-core-gray-950')}
            />
            <div className="w-full py-2">
              <div
                className={classNames('photon-caption-sm text-sm', isDarkMode ? 'text-white' : 'text-core-gray-950')}
              >
                Card
              </div>
              {savedPaymentInfo ? (
                <PrefilledCreditCardInput
                  className="border !border-core-gray-200 !bg-white"
                  savedPayment={savedPaymentInfo as PaymentInfo}
                  changeCard={() => setSavedPaymentInfo(undefined)}
                />
              ) : (
                <div className="mt-1 h-12 rounded border !border-core-gray-200 bg-white px-2 py-3">
                  <CardElement options={options} />
                </div>
              )}
            </div>
            <Button
              aria-label="pay-button"
              variant={isDarkMode ? 'oxide-bright' : 'oxide-primary'}
              className="mt-4 flex w-full justify-center rounded-xl px-4 py-3"
              disabled={isSubmitting || isSubmittingWithWallet}
            >
              {price}
            </Button>
          </div>
        )}
        <div className="-mb-4 pt-4 text-center text-red">
          {errorMessage ? errorMessage : errorMessageWithWallet ? errorMessageWithWallet : ''}
        </div>
      </form>
    </>
  )
}

const options = {
  style: {
    base: {
      color: '#141414',
      colorText: '#fff',
      backgroundColor: '#FFF',
      borderRadius: '8px',
      height: '48px',
      fontSize: '16px',
      fontFamily: 'inherit',
      lineHeight: '28px',
      '::placeholder': {
        color: '#7B7979',
        colorText: '#7B7979',
        fontSize: '16px',
        fontFamily: 'inherit',
      },
    },
  },
}
